import { PrivyProvider } from '@privy-io/react-auth'
import { WagmiProvider } from '@privy-io/wagmi'
import { createConfig, http } from 'wagmi'
import { bsc, bscTestnet } from 'wagmi/chains' //binance
import { injected, coinbaseWallet } from 'wagmi/connectors'

// Determine which chain to use based on environment
const CHAIN_ID = import.meta.env.VITE_CHAIN_ID ? parseInt(import.meta.env.VITE_CHAIN_ID) : 56
const IS_TESTNET = CHAIN_ID === 97
const ACTIVE_CHAIN = IS_TESTNET ? bscTestnet : bsc

// RPC URL - utiliser un provider custom si disponible
const BASE_RPC_URL = import.meta.env.VITE_RPC_URL || 
  (IS_TESTNET 
    ? 'https://data-seed-prebsc-1-s1.binance.org:8545/' 
    : 'https://bsc-mainnet.infura.io/v3/d2ea31ea15274181a6181dc2e99cd4d6')

console.log(`🔧 Network Config: ${IS_TESTNET ? 'BSC Testnet' : 'BSC Mainnet'} (Chain ID: ${CHAIN_ID})`)
console.log(`📡 RPC URL: ${BASE_RPC_URL}`)

// Configuration Wagmi pour Privy
export const wagmiConfig = createConfig({
  chains: [ACTIVE_CHAIN] as const,
  connectors: [
    injected(),
    coinbaseWallet({ appName: 'KBETS' }),
  ],
  transports: {
    [ACTIVE_CHAIN.id]: http(BASE_RPC_URL, {
      batch: {
        wait: 100,
      },
      retryCount: 3,
      retryDelay: 1000,
    }),
  },
})

// Export for use in other files
export { ACTIVE_CHAIN, IS_TESTNET, CHAIN_ID }

// Configuration Privy
const isBrowser = typeof window !== 'undefined'
const hostname = isBrowser ? window.location.hostname : ''
const isHttps = isBrowser && window.location.protocol === 'https:'
const isLocalhost = ['localhost', '127.0.0.1'].includes(hostname) || hostname === '0.0.0.0'
const isLan = /^192\.168\./.test(hostname) || hostname === '0.0.0.0'
// Privy autorisé en HTTPS et sur localhost uniquement (pas sur LAN en HTTP)
export const isPrivyEnv = isHttps || isLocalhost

// Configuration Privy avec gestion d'erreur améliorée
const getPrivyAppId = () => {
  const appId = import.meta.env.VITE_PRIVY_APP_ID
  
  // Validation basique de l'ID Privy
  if (!appId || appId.length < 10) {
    console.warn('Invalid Privy App ID detected:', appId)
    return null
  }
  
  return appId
}

export const privyConfig = {
  appId: getPrivyAppId(),
  config: {
        // Login methods - Wallet only
        loginMethods: ['wallet'] as ('email' | 'wallet' | 'sms' | 'google' | 'twitter')[],
    
    // Appearances
    appearance: {
      theme: 'dark' as const,
      accentColor: '#4FFFB0' as const, // KBETS teal accent
      logo: 'https://kbets.gg/favicon.ico', // Update with actual KBETS logo URL when available
    },
    
    // Legal
    legal: {
      termsAndConditionsUrl: 'https://kbets.gg/terms',
      privacyPolicyUrl: 'https://kbets.gg/privacy',
    },
    
    // MFA
    mfa: {
      noPromptOnMfaRequired: false,
    },
    
    // Embedded wallets configuration
    ...(isHttps
      ? {
          embeddedWallets: {
            ethereum: {
              createOnLogin: 'users-without-wallets' as const,
            },
          },
        }
      : {
          embeddedWallets: {
            ethereum: {
              createOnLogin: 'off' as const,
            },
          },
        }),
    
      // Default chain configuration - Based on environment
    defaultChain: ACTIVE_CHAIN,
    
    // Supported chains - Based on environment
    supportedChains: [ACTIVE_CHAIN],
  },
}

declare module 'wagmi' {
  interface Register {
    config: typeof wagmiConfig
  }
}
